// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Edit from "@insite/mobius/Icons/Edit";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { StepperContext } from "@keller/App/Widgets/CheckoutStepper/StepperContext";
import { mergeWithCss } from "@keller/XDL/Utilities";
import { iconLinkCommonStyles } from "@keller/XDL/Utilities/links";
import React, { useContext, useState } from "react";
import { css } from "styled-components";

interface CheckoutSectionHeadingProps {
    label?: string;
    moveToStep?: number;
    extendedStyles?: CheckoutSectionHeadingStyles;
}

export interface CheckoutSectionHeadingStyles {
    headingTextGridItem?: GridItemProps;
    headingText?: TypographyProps;
    editLink?: LinkPresentationProps;
}

export const checkoutSectionHeadingStyles: CheckoutSectionHeadingStyles = {
    headingTextGridItem: {
        width: 12,
        css: css`
            align-items: center;
            justify-content: space-between;
        `,
    },
    headingText: {
        variant: "h5",
        as: "h2",
        css: css`
            margin-bottom: 0;
        `,
    },
    editLink: {
        ...iconLinkCommonStyles(Edit),
    },
};

const CheckoutSectionHeading = ({ label, moveToStep, extendedStyles }: CheckoutSectionHeadingProps) => {
    const [styles] = useState(() => mergeWithCss(checkoutSectionHeadingStyles, extendedStyles));
    const { moveToStepHandler } = useContext(StepperContext);
    const moveTo = () => {
        if (typeof moveToStep !== "number") {
            return;
        }

        moveToStepHandler(moveToStep);
    };

    return (
        <GridItem {...styles.headingTextGridItem}>
            <Typography {...styles.headingText}>{label}</Typography>
            {typeof moveToStep === "number" && (
                <Link {...styles.editLink} aria-label={`move-to-${label}`} onClick={moveTo}>
                    {translate("Change")}
                </Link>
            )}
        </GridItem>
    );
};

export default CheckoutSectionHeading;
